import React, { useEffect, useMemo } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import SelectedParticipantChip from "../SelectedParticipantChip";
import Navbar from "app/components/Navbar";
import { useHistory } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { toast } from "react-toastify";
import { defaultSelection } from "../helpers";
import { seedFacility } from "./seedFacility";
import {
  resetBookSession,
  setBookSession,
} from "app/actions/scheduling/bookSession";
import { sortBy } from "lodash";
import { cls } from "app/utils";
import useFacilityLessonRates from "../hooks/useFacilityLessonRates";
import usePackageCredits from "../hooks/usePackageCredits";
import { Loading } from "app/components/Wrappers";

import SelectFacility from "./SelectFacility";
import FacilityBooking from "../AllCoachesPublicSession/_components/FacilityBooking";
import { facilitySummary } from "../AllCoachesPublicSession/_components/coachMeta";

const AllCoachesPrivateSession = ({ bookLessonData }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { self: user } = useSelector(
    (state: RootState) => state.user,
    shallowEqual,
  );

  const bookLessonState = useSelector((state: RootState) => state.bookSession);

  const facility = bookLessonState.facility;
  const facilityBookingEnabled = facility?.featureFlags?.facilityLevelBookingOptions === true;

  const { rates: facilityRates, loading: facilityRatesLoading } = useFacilityLessonRates(
    facility?.id,
    facilityBookingEnabled
  );

  const isAthlete = user?.kind === "athlete" || user?.kind === "other";
  // CFTB-3318: scope the package-credit indicator to the selected family member
  // (if any) so it reflects the participant's packages, not the head's.
  const { credits: packageCredits, loading: packageCreditsLoading } =
    usePackageCredits(
      Boolean(user?.id) && isAthlete,
      bookLessonState.participantAthleteId,
    );

  const lessonProperties = useMemo(() => {
    const pricing =
      bookLessonState.pricingValue ||
      bookLessonData?.pricingValues[bookLessonState?.coach?.id]?.find(
        (pv) => pv.defaultRate,
      );
    return {
      facility: bookLessonState.facility,
      coach: bookLessonState.coach,
      duration: pricing,
      pricingValue: pricing,
      date: null,
    };
  }, [bookLessonState, bookLessonData]);


  const coachesForSelect = useMemo(() => {
    const facilityId = lessonProperties?.facility?.id;
    if (!facilityId || !bookLessonData?.coaches?.[facilityId]) {
      return [];
    }

    const sortedCoaches = sortBy(bookLessonData.coaches[facilityId], ["order_number"]).filter(
      (c) => c.facilityId === facilityId,
    );

    if (facilityBookingEnabled && facilityRates.length > 0) {
      return sortedCoaches.map(coach => ({
        ...coach,
        lessonRates: facilityRates,
        currencySymbol: '$'
      }));
    }

    return sortedCoaches;
  }, [lessonProperties?.facility?.id, bookLessonData, facilityBookingEnabled, facilityRates]);

  // The switcher's second line: the facility's coaches and session types.
  const { coachCount, sessionTypeCount } = useMemo(
    () => facilitySummary(coachesForSelect),
    [coachesForSelect],
  );

  const onBackClickHandler = () => {
    history.push("/sessions");
  };

  useEffect(() => {
    if (user !== null && user.kind !== "athlete" && user.kind !== "other") {
      history.push("/");
      toast.error(t("You do not have access to that page."));
    }
  }, [user]);

  useEffect(() => {
    // Clear whatever a previous booking attempt left behind, then seed this
    // one. CN-1323: keep who the booking is for — chosen in the picker modal
    // before this screen — which resetBookSession would otherwise wipe.
    const participantAthleteId = bookLessonState.participantAthleteId;
    // ...and the facility they are on: this screen is where the back arrow of
    // every screen below it leads, and seeding from scratch took the *first*
    // facility the athlete belongs to rather than the one they were looking at.
    const stayOnFacility = seedFacility(
      bookLessonData?.facilities,
      bookLessonState.facility?.id,
    );

    dispatch(resetBookSession());

    const initialData = defaultSelection(bookLessonData, stayOnFacility, t);

    const lessonState = {
      ...initialData,
      date: initialData.date.toISODate(),
      time: null,
      public: true,
      ...(participantAthleteId ? { participantAthleteId } : {}),
    };

    dispatch(setBookSession(lessonState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  if ((facilityBookingEnabled && facilityRatesLoading) || packageCreditsLoading) {
    return (
      <div
        className={cls(
          styles.container,
          "booking-flow-page",
          "booking-flow-full-width",
        )}
      >
        <Navbar
          label={t("Book a Session")}
          hasSearch={false}
          hasNotifications={false}
          onBackClick={onBackClickHandler}
          className={styles.navbar_wrapper}
        />
        <SelectFacility facilities={bookLessonData?.facilities} />
        <div className={styles.test}>
          <Loading isLoading loadType="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div
      // `booking-flow-page`: the facility booking screen sits on the same grey as
      // the rest of the flow (frame 526:28677), rather than the app's own shade.
      // `booking-flow-full-width` unconditionally: SiteWrapper narrows its child
      // container unless a page opts out, and this one was opting out only when the
      // facility had more than one coach. Since CFTB-3478 the card grid is what a
      // single-coach facility gets too, so the page rendered inside the narrow
      // container - the whole screen indented, the one card at half its width.
      className={cls(
        styles.container,
        "booking-flow-page",
        "booking-flow-full-width",
      )}
    >
      <Navbar
        label={t("Book a Session")}
        hasSearch={false}
        hasNotifications={false}
        onBackClick={onBackClickHandler}
        className={styles.navbar_wrapper}
      >
        <SelectedParticipantChip
          participantAthleteId={bookLessonState.participantAthleteId}
        />
      </Navbar>
      {/*
        CN-2652: the redesigned picker — the same component the public facility
        page uses, with this flow's facility switcher and its own booking
        destination.

        CFTB-3478: for every facility, not only one with coaches to choose
        between. A single-coach facility used to keep the old options panel on the
        grounds that there was nothing to pick, and a coach with no options then
        got the old screen's bare "No available booking options" — the pre-redesign
        page, in the middle of the redesigned flow. It gets a card like everyone
        else now, and the card leads to the coach's page.
      */}
      {bookLessonState?.facility?.id ? (
        <div className={styles.test}>
          <FacilityBooking
            coaches={coachesForSelect}
            timezone=""
            packageCredits={packageCredits}
            showTitleRow={false}
            facilitySwitcher={
              <SelectFacility
                facilities={bookLessonData?.facilities}
                embedded
                // The frame draws the counts as the switcher's second line, so
                // they go in with it rather than under the whole control.
                subtitle={
                  <>
                    {t("{{count}} coaches", {
                      count: coachCount,
                    })}
                    {" · "}
                    {t("{{count}} session types", {
                      count: sessionTypeCount,
                    })}
                  </>
                }
              />
            }
            bookingRoute={(coach) => `/sessions/coach/${coach.externalId}`}
            // A chosen slot goes straight to the booking screen, which opens on
            // it — the coach's page would throw the choice away.
            slotBookingRoute={() => "/sessions/new"}
            isPublicPage={false}
          />
        </div>
      ) : (
        // Nothing chosen yet: the switcher alone. The coach list that used to sit
        // here was the pre-redesign panel, and it only ever drew once a facility
        // was chosen — which is the branch above now.
        <SelectFacility facilities={bookLessonData?.facilities} />
      )}
    </div>
  );
};

export default AllCoachesPrivateSession;
